import React from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useLang } from "../context/LangContext";
import { ShieldCheck, MessageCircle, Zap, TrendingUp, Target, Users2, Sparkles, ArrowRight, Check, ChevronRight, Clock, Award } from "lucide-react";

const WA_NUMBER = process.env.REACT_APP_PARTNER_WA || "+213770000000";

export default function ForAgencies() {
  const { t } = useLang();
  const g = t("agc") || {};
  const waMsg = encodeURIComponent(
    `${g.ctaBecome || "Become a Partner Agency"} — ${g.sub || ""}`
  );
  const waLink = `https://wa.me/${WA_NUMBER.replace(/[^0-9]/g, "")}?text=${waMsg}`;

  return (
    <div className="min-h-screen">
      <Header />

      {/* HERO */}
      <section className="pt-32 pb-20 md:pb-28 bg-gradient-to-b from-[#F4F9F6] to-white">
        <div className="max-w-6xl mx-auto px-5 md:px-10">
          <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-[#1B8F5A] bg-[#1B8F5A]/10 px-3 py-1.5 rounded-full">
            <Sparkles size={13} /> {g.badge}
          </span>
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold tracking-tight text-[#111827] mt-6 max-w-4xl leading-[1.05]">
            {g.h1a}<br /><span className="text-[#1B8F5A]">{g.h1b}</span>
          </h1>
          <p className="text-lg md:text-xl text-slate-600 mt-6 max-w-2xl leading-relaxed">{g.sub}</p>
          <div className="mt-10 flex flex-col sm:flex-row gap-3">
            <a href={waLink} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-2 bg-[#1B8F5A] hover:bg-[#157047] text-white px-7 py-4 rounded-full font-semibold text-base transition" data-testid="agency-cta-primary">
              {g.ctaBecome} <ArrowRight size={17} />
            </a>
            <a href={waLink} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-2 bg-white hover:bg-slate-50 border border-slate-200 text-[#111827] px-7 py-4 rounded-full font-semibold text-base transition" data-testid="agency-cta-whatsapp">
              <MessageCircle size={17} className="text-[#25D366]" /> {g.ctaWa}
            </a>
          </div>
          <div className="mt-12 flex items-center gap-6 text-sm text-slate-500 flex-wrap">
            <span className="flex items-center gap-2"><Check size={15} className="text-[#1B8F5A]" /> {g.bullet1}</span>
            <span className="flex items-center gap-2"><Check size={15} className="text-[#1B8F5A]" /> {g.bullet2}</span>
            <span className="flex items-center gap-2"><Check size={15} className="text-[#1B8F5A]" /> {g.bullet3}</span>
          </div>
        </div>
      </section>

      {/* PROBLEM */}
      <section className="py-20 md:py-28">
        <div className="max-w-6xl mx-auto px-5 md:px-10">
          <div className="max-w-2xl mb-12">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">{g.problemLabel}</span>
            <h2 className="text-3xl md:text-5xl font-bold text-[#111827] mt-3 leading-tight">{g.problemTitle}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {[[g.p1t, g.p1b], [g.p2t, g.p2b], [g.p3t, g.p3b], [g.p4t, g.p4b]].map(([h, b], i) => (
              <div key={i} className="bg-white border border-slate-200 rounded-2xl p-7 hover:border-slate-300 transition">
                <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center mb-4">
                  <span className="text-red-600 font-bold">×</span>
                </div>
                <h3 className="font-bold text-lg text-[#111827]">{h}</h3>
                <p className="text-slate-600 mt-2 leading-relaxed">{b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SOLUTION */}
      <section className="py-20 md:py-28 bg-[#0F1F17] text-white">
        <div className="max-w-6xl mx-auto px-5 md:px-10">
          <div className="max-w-2xl mb-14">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#1B8F5A]">{g.solLabel}</span>
            <h2 className="text-3xl md:text-5xl font-bold text-white mt-3 leading-tight">{g.solTitle}</h2>
            <p className="text-slate-300 mt-5 text-lg leading-relaxed">{g.solDesc}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[[Target, g.s1t, g.s1b], [TrendingUp, g.s2t, g.s2b], [MessageCircle, g.s3t, g.s3b]].map(([I, h, b], i) => (
              <div key={i} className="bg-white/5 border border-white/10 rounded-2xl p-7 backdrop-blur">
                <I size={26} className="text-[#1B8F5A]" />
                <h3 className="font-bold text-lg text-white mt-4">{h}</h3>
                <p className="text-slate-400 mt-2 leading-relaxed">{b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-20 md:py-28">
        <div className="max-w-6xl mx-auto px-5 md:px-10">
          <div className="max-w-2xl mb-14">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#1B8F5A]">{g.hwLabel}</span>
            <h2 className="text-3xl md:text-5xl font-bold text-[#111827] mt-3 leading-tight">{g.hwTitle}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[["01", g.hw1t, g.hw1b], ["02", g.hw2t, g.hw2b], ["03", g.hw3t, g.hw3b]].map(([n, h, b]) => (
              <div key={n} className="relative">
                <div className="text-6xl font-bold text-[#1B8F5A]/15">{n}</div>
                <h3 className="text-xl font-bold text-[#111827] mt-2">{h}</h3>
                <p className="text-slate-600 mt-3 leading-relaxed">{b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="py-20 md:py-28 bg-[#F4F9F6]">
        <div className="max-w-6xl mx-auto px-5 md:px-10">
          <div className="max-w-2xl mb-12">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#1B8F5A]">{g.benLabel}</span>
            <h2 className="text-3xl md:text-5xl font-bold text-[#111827] mt-3 leading-tight">{g.benTitle}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {[[Users2, g.b1t, g.b1b], [MessageCircle, g.b2t, g.b2b], [TrendingUp, g.b3t, g.b3b], [Zap, g.b4t, g.b4b], [Award, g.b5t, g.b5b], [Clock, g.b6t, g.b6b]].map(([I, h, b], i) => (
              <div key={i} className="bg-white rounded-2xl p-7 border border-slate-100">
                <div className="w-11 h-11 rounded-xl bg-[#1B8F5A]/10 flex items-center justify-center">
                  <I size={20} className="text-[#1B8F5A]" />
                </div>
                <h3 className="font-bold text-[#111827] mt-5">{h}</h3>
                <p className="text-slate-600 mt-2 text-sm leading-relaxed">{b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TRUST */}
      <section className="py-20 md:py-28">
        <div className="max-w-4xl mx-auto px-5 md:px-10 text-center">
          <ShieldCheck size={42} className="text-[#1B8F5A] mx-auto" />
          <h2 className="text-3xl md:text-5xl font-bold text-[#111827] mt-6 leading-tight">{g.trustTitle}</h2>
          <p className="text-slate-600 mt-5 text-lg leading-relaxed">{g.trustDesc}</p>
          <div className="mt-10 grid grid-cols-3 gap-4 max-w-2xl mx-auto">
            {[[g.stat1a, g.stat1b], [g.stat2a, g.stat2b], [g.stat3a, g.stat3b]].map(([a, b], i) => (
              <div key={i} className="bg-[#F4F9F6] rounded-2xl p-5 text-left">
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{a}</div>
                <div className="font-bold text-[#1B8F5A] text-lg">{b}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-20 md:py-28 bg-gradient-to-br from-[#1B8F5A] to-[#0F5A39] text-white">
        <div className="max-w-4xl mx-auto px-5 md:px-10 text-center">
          <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] bg-white/15 px-3 py-1.5 rounded-full">
            <Sparkles size={13} /> {g.finalBadge}
          </span>
          <h2 className="text-4xl md:text-6xl font-bold mt-6 leading-tight">{g.finalTitle}</h2>
          <p className="text-white/90 mt-6 text-lg md:text-xl max-w-2xl mx-auto leading-relaxed">{g.finalSub}</p>
          <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
            <a href={waLink} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-2 bg-white text-[#1B8F5A] hover:bg-slate-100 px-8 py-4 rounded-full font-bold text-base transition" data-testid="agency-cta-final">
              <MessageCircle size={17} /> {g.finalCta}
            </a>
            <Link to="/register" className="inline-flex items-center justify-center gap-2 bg-transparent border-2 border-white/40 text-white hover:bg-white/10 px-8 py-4 rounded-full font-bold text-base transition">
              {g.finalCreate} <ChevronRight size={17} />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
